import React, { useState } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  Lock,
  Mail,
  User as UserIcon,
  Building2,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  LogOut,
  RefreshCw,
  Sparkles,
  KeyRound,
  ExternalLink
} from 'lucide-react';
import { useGst } from '../context/GstContext';

export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    user,
    userRole,
    isAuthorized,
    authLoading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    sendResetPassword,
    logout
  } = useGst();

  const [mode, setMode] = useState<'signin' | 'register' | 'forgot'>('signin');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  // 1. Loading State
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0b0c0e] text-white flex flex-col items-center justify-center p-4">
        <div className="relative mb-6">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-tr from-cyan-600 to-cyan-400 flex items-center justify-center font-bold text-black text-2xl shadow-xl shadow-cyan-500/20">
            V
          </div>
          <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-[#0b0c0e] flex items-center justify-center">
            <RefreshCw className="h-3.5 w-3.5 text-cyan-400 animate-spin" />
          </div>
        </div>
        <h2 className="text-sm font-bold text-white tracking-wide">
          Verve AI Labs — GST Compliance Platform
        </h2>
        <p className="text-xs text-slate-400 mt-1">
          Verifying tenant workspace credentials and RBAC authorization...
        </p>
      </div>
    );
  }

  // 2. Not Authenticated: Render Authentication Portal
  if (!user) {
    const handleGoogleAuth = async () => {
      setError('');
      setSuccessMsg('');
      setSubmitting(true);
      try {
        await signInWithGoogle();
      } catch (err: any) {
        setError(err.message || 'Google sign-in failed');
      } finally {
        setSubmitting(false);
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setSuccessMsg('');
      setSubmitting(true);

      try {
        if (mode === 'signin') {
          await signInWithEmail(email, password);
        } else if (mode === 'register') {
          if (!name.trim()) throw new Error('Please enter your full name');
          if (!companyName.trim()) throw new Error('Please enter your company legal name');
          await signUpWithEmail(email, password, name, companyName);
          setSuccessMsg('Account created successfully! Logging you into your client workspace...');
        } else if (mode === 'forgot') {
          await sendResetPassword(email);
          setSuccessMsg('Password reset link sent to your email. Check your inbox!');
        }
      } catch (err: any) {
        setError(err.message || 'Authentication error');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen bg-[#0b0c0e] text-[#f7f7f8] flex flex-col justify-between p-4 sm:p-6 selection:bg-cyan-500/20 selection:text-cyan-200">
        {/* Top Sovereign Navigation Banner */}
        <header className="w-full max-w-6xl mx-auto flex items-center justify-between pb-4 border-b border-white/[0.06]">
          <a
            href="https://verveailabs.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 group transition"
            title="Visit Verve AI Labs Sovereign Cloud & AI Systems (verveailabs.com)"
          >
            <div className="relative h-8 w-8 rounded-xl bg-gradient-to-tr from-[#07080a] via-[#111317] to-[#0e1015] border border-cyan-500/40 p-1.5 flex items-center justify-center shadow-md shadow-cyan-500/20 group-hover:border-cyan-400 transition">
              <img
                src="/logo.svg"
                alt="Verve AI Labs"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (target.src.endsWith(".svg")) target.src = "/logo.png";
                }}
              />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-white tracking-tight">
                  Verve <span className="text-cyan-400">AI Labs</span>
                </span>
                <span className="text-[9px] font-mono uppercase px-1.5 py-0.2 rounded border border-cyan-500/30 bg-cyan-950/60 text-cyan-300">
                  HQ
                </span>
              </div>
              <span className="text-[10px] text-slate-500 font-mono group-hover:text-cyan-300 transition flex items-center gap-0.5">
                <span>verveailabs.com</span>
                <ExternalLink className="h-2.5 w-2.5" />
              </span>
            </div>
          </a>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-950/40 border border-cyan-500/25 text-xs text-cyan-300">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="font-medium">GST Statutory Enterprise Console</span>
            </span>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-md rounded-3xl border border-[rgba(247,247,248,0.1)] bg-[#131316] p-8 shadow-2xl space-y-6">
            {/* Header Brand */}
            <div className="text-center space-y-3">
              {/* Official Glowing Verve AI Logo Box */}
              <a
                href="https://verveailabs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block group focus:outline-none"
                title="Verve AI Labs (verveailabs.com)"
              >
                <div className="relative mx-auto h-16 w-16 rounded-2xl bg-gradient-to-tr from-[#07080a] via-[#111317] to-[#0e1015] border border-cyan-500/40 p-3 shadow-[0_0_25px_rgba(6,182,212,0.25)] flex items-center justify-center group-hover:border-cyan-400 group-hover:shadow-[0_0_35px_rgba(6,182,212,0.4)] transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/20 via-sky-400/10 to-indigo-600/30 rounded-2xl opacity-75 group-hover:opacity-100 transition-opacity" />
                  <img
                    src="/logo.svg"
                    alt="Verve AI Labs Logo"
                    className="relative z-10 h-10 w-10 object-contain drop-shadow-[0_0_12px_rgba(6,182,212,0.6)] group-hover:scale-105 transition-transform"
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (target.src.endsWith(".svg")) target.src = "/logo.png";
                    }}
                  />
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-white/15 pointer-events-none" />
                </div>
              </a>

              <div>
                <h1 className="text-xl font-bold text-white tracking-wide flex items-center justify-center gap-2">
                  <span>Verve AI Labs</span>
                  <span className="text-cyan-400 font-normal">| GST Portal</span>
                </h1>

                {/* Subsidiary Pill */}
                <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-cyan-950/60 border border-cyan-500/30 text-xs text-cyan-300 shadow-sm">
                  <span className="text-slate-400">A Subsidiary Venture of</span>
                  <a
                    href="https://verveailabs.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-cyan-300 hover:text-white underline decoration-cyan-500/50 transition flex items-center gap-0.5"
                  >
                    <span>verveailabs.com</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed max-w-sm mx-auto pt-1">
                Statutory GST & Accounting Compliance Console &bull; Client & CA Admin Access Only
              </p>
            </div>

            {/* Error / Success Notifications */}
            {error && (
              <div className="flex items-center gap-2 rounded-xl bg-rose-500/10 border border-rose-500/20 p-3 text-xs text-rose-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {successMsg && (
              <div className="flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 text-xs text-emerald-300">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>{successMsg}</span>
              </div>
            )}

            {/* Google 1-Click Sign-In */}
            <div>
              <button
                type="button"
                onClick={handleGoogleAuth}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2.5 rounded-xl border border-white/10 bg-[#0e1015] hover:bg-white/[0.04] py-2.5 text-xs font-semibold text-white transition disabled:opacity-50"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
                <span>Continue with Google Workspace</span>
              </button>
            </div>

            <div className="relative flex items-center justify-center">
              <div className="border-t border-white/10 w-full" />
              <span className="bg-[#131316] px-3 text-[10px] uppercase font-bold text-slate-500">
                Or With Work Email
              </span>
              <div className="border-t border-white/10 w-full" />
            </div>

            {/* Email / Password Form */}
            <form onSubmit={handleSubmit} className="space-y-3.5 text-xs">
              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                      Full Name
                    </label>
                    <div className="relative flex items-center">
                      <UserIcon className="absolute left-3 h-4 w-4 text-slate-500" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Rahul Sharma"
                        className="w-full rounded-xl border border-white/10 bg-[#0e1015] pl-9 pr-3 py-2 text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                      Company / Legal Entity Name
                    </label>
                    <div className="relative flex items-center">
                      <Building2 className="absolute left-3 h-4 w-4 text-slate-500" />
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="e.g. Acme Innovations Pvt Ltd"
                        className="w-full rounded-xl border border-white/10 bg-[#0e1015] pl-9 pr-3 py-2 text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  Corporate Email
                </label>
                <div className="relative flex items-center">
                  <Mail className="absolute left-3 h-4 w-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full rounded-xl border border-white/10 bg-[#0e1015] pl-9 pr-3 py-2 text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              {mode !== 'forgot' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-semibold text-slate-300">Password</label>
                    {mode === 'signin' && (
                      <button
                        type="button"
                        onClick={() => {
                          setError('');
                          setMode('forgot');
                        }}
                        className="text-[11px] text-cyan-400 hover:underline"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative flex items-center">
                    <Lock className="absolute left-3 h-4 w-4 text-slate-500" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      className="w-full rounded-xl border border-white/10 bg-[#0e1015] pl-9 pr-3 py-2 text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none"
                      required
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 py-2.5 font-bold text-black transition hover:opacity-90 disabled:opacity-50 mt-2"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Processing...</span>
                  </span>
                ) : mode === 'signin' ? (
                  'Sign In to Compliance Vault'
                ) : mode === 'register' ? (
                  'Create Client Workspace'
                ) : (
                  'Send Password Reset Link'
                )}
              </button>
            </form>

            {/* Mode Switcher */}
            <div className="pt-2 text-center text-xs text-slate-400">
              {mode === 'signin' && (
                <p>
                  New business client?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setError('');
                      setMode('register');
                    }}
                    className="font-semibold text-cyan-400 hover:underline"
                  >
                    Register Client Workspace
                  </button>
                </p>
              )}
              {mode === 'register' && (
                <p>
                  Already have access?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setError('');
                      setMode('signin');
                    }}
                    className="font-semibold text-cyan-400 hover:underline"
                  >
                    Sign In
                  </button>
                </p>
              )}
              {mode === 'forgot' && (
                <p>
                  Remembered your password?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setError('');
                      setMode('signin');
                    }}
                    className="font-semibold text-cyan-400 hover:underline"
                  >
                    Back to Sign In
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Security & Corporate Subsidiary Footer */}
        <footer className="py-6 border-t border-white/[0.06] text-center space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-6 text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-cyan-400" />
              256-Bit Cryptographic Vault Isolation
            </span>
            <span className="flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-emerald-400" />
              CLIENT & ADMIN Strict RBAC
            </span>
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
              MCA Audit Trail Compliant
            </span>
          </div>

          <div className="text-[11px] text-slate-500 flex flex-wrap items-center justify-center gap-2">
            <span>&copy; {new Date().getFullYear()} Verve AI Labs Private Limited.</span>
            <span>&bull;</span>
            <span>GST Platform operates as an enterprise subsidiary unit of</span>
            <a
              href="https://verveailabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 underline font-semibold transition"
            >
              verveailabs.com
            </a>
          </div>
        </footer>
      </div>
    );
  }

  // 3. Authenticated BUT Not Authorized (Not CLIENT and Not ADMIN)
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#0b0c0e] text-[#f7f7f8] flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-3xl border border-rose-500/30 bg-[#131316] p-8 shadow-2xl text-center space-y-5">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
            <ShieldAlert className="h-7 w-7" />
          </div>

          <div className="space-y-1">
            <h2 className="text-lg font-bold text-white">Client / Admin Role Required</h2>
            <p className="text-xs text-slate-400">
              Access to the Verve AI Labs GST Compliance Platform is strictly guarded.
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0e1015] p-4 text-xs space-y-2 text-left">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Authenticated Email:</span>
              <span className="font-mono font-bold text-white">{user.email}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Assigned Role:</span>
              <span className="rounded bg-rose-500/20 text-rose-300 px-2 py-0.5 text-[10px] font-bold uppercase font-mono">
                {userRole} (Unentitled)
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Required Role:</span>
              <span className="rounded bg-cyan-500/20 text-cyan-300 px-2 py-0.5 text-[10px] font-bold uppercase font-mono">
                CLIENT or ADMIN
              </span>
            </div>
          </div>

          <p className="text-xs text-slate-400 leading-relaxed text-left">
            Your Google/Firebase account has been authenticated, but your profile has not yet been assigned to an active corporate Client organization or CA Admin console. Standard user accounts cannot access company GST vaults, invoice extraction, or statutory returns.
          </p>

          <div className="space-y-2.5 pt-2">
            <a
              href={`mailto:support@verveailabs.com?subject=Request%20GST%20Client%20Role%20Access%20for%20${encodeURIComponent(user.email || '')}&body=Hello%20Verve%20AI%20Team%2C%0A%0APlease%20assign%20CLIENT%20role%20to%20my%20account%20(${encodeURIComponent(user.email || '')})%20with%20UID%3A%20${user.uid}%20for%20GST%20Compliance%20access.%0A%0AThank%20you!`}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 py-2.5 text-xs font-bold text-black transition"
            >
              <Mail className="h-4 w-4" />
              <span>Request Client Access via Support</span>
            </a>

            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#0e1015] hover:bg-white/5 py-2.5 text-xs font-semibold text-slate-300 transition"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign In with a Different Account</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 4. Fully Authorized (User is logged in with CLIENT or ADMIN role)
  return <>{children}</>;
};
